import { inject } from '@angular/core';
import { Router, ActivatedRouteSnapshot, UrlTree } from '@angular/router';
import { AuthStateService } from '../services/auth-state.service';
import { StorageService } from '../services/storage.service';
import { TenantService } from '../services/tenant.service';
import { firstValueFrom } from 'rxjs';

const signInReturnShortNameKey = 'sign_in_return_short_name';

function readSignInShortNameFromSession(): string | null {
  return sessionStorage.getItem(signInReturnShortNameKey)?.trim()
    || sessionStorage.getItem('tenant_short_name')?.trim()
    || null;
}

export const authGuard = (route: ActivatedRouteSnapshot): boolean | UrlTree => {
  const authStateService = inject(AuthStateService);
  const router = inject(Router);
  const storageService = inject(StorageService);
  const tenantService = inject(TenantService);

  if (authStateService.isLoggedIn) {
    // Check if user is a patient representative who needs to select a patient
    const associatedPatients = storageService.getSessionStorage('associated_patients');
    
    // If associated_patients exists, user must select a patient first
    // But allow navigation to /associatedPatients and /quick-activate routes
    if (associatedPatients && Array.isArray(associatedPatients) && associatedPatients.length > 0) {
      // Check the route path being navigated to (not current URL)
      const targetPath = route.routeConfig?.path;
      
      // Allow access to associated patients page and quick activate page
      // Redirect all other routes to associated patients page
      if (targetPath !== 'associatedPatients' && targetPath !== 'quick-activate') {
        router.navigate(['/associatedPatients']);
        return false;
      }
    }
    
    return true;
  }

  const shortName = readSignInShortNameFromSession();
  if (shortName) {
    return router.createUrlTree([`/${shortName}`], { queryParams: { sessionEnded: '1' } });
  }

  const tenantCode = storageService.getSessionStorage('tenant_code');

  if (tenantCode) {
    firstValueFrom(tenantService.getTenantByCode(tenantCode)).then(tenant => {
      if (tenant?.tenantShortName) {
        router.navigate([`/${tenant.tenantShortName}`]);
      } else {
        router.navigate(['/page-not-found']);
      }
    }).catch(() => {
      router.navigate(['/page-not-found']);
    });
  } else {
    router.navigate(['/page-not-found']);
  }

  return false;
};
